import { type Ref, nextTick, ref } from 'vue';

import type CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import type {
  CityState,
  GeolocationPersistence,
} from '@/core/shared/geolocation/geolocation.persistence';

type Focusable = { focus: () => void };

/**
 * City and state can be filled in either order. Once a state is *selected* (not while typing or
 * browsing the list), a city that provably does not exist there is cleared and refocused so it can
 * be replaced. A failed or slow lookup keeps whatever the user typed (ONE-7106, David's review).
 * A picked city suggestion is already a known pair, so it replaces both fields with no check.
 */
export function useCityStatePairing(
  service: CopilotActivationService,
  geolocation: GeolocationPersistence,
): {
  cityInput: Ref<Focusable | null>;
  handleStateSelected: (state: string | null) => Promise<void>;
  handleCitySelected: (pair: CityState) => void;
} {
  const cityInput = ref<Focusable | null>(null);

  async function handleStateSelected(state: string | null): Promise<void> {
    service.state = state ?? '';
    if (!state) return;

    const city = service.city?.trim();
    if (!city) return;

    const pairing = await geolocation.checkCityStatePairing(city, state);
    if (pairing !== 'invalid') return;
    // The user moved on while we were checking; their newer input wins.
    if (service.state !== state || service.city?.trim() !== city) return;

    service.city = '';
    await nextTick();
    cityInput.value?.focus();
  }

  function handleCitySelected(pair: CityState): void {
    service.city = pair.city;
    service.state = pair.state;
  }

  return { cityInput, handleStateSelected, handleCitySelected };
}
